import React from 'react';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';

export interface DropdownOption {
  label: string;
  value: any;
  disabled?: boolean;
  icon?: string;
}

interface GlobalDropdownFieldProps {
  label?: string;
  value: any;
  onChange: (value: any) => void;
  options: DropdownOption[];
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  icon?: string;
  fullWidth?: boolean;
  filter?: boolean;
  showClear?: boolean;
  optionLabel?: string;
  optionValue?: string;
}

export const GlobalDropdownField: React.FC<GlobalDropdownFieldProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder = 'Select an option',
  required = false,
  disabled = false,
  error = '',
  icon,
  fullWidth = true,
  filter = false,
  showClear = false,
  optionLabel = 'label',
  optionValue = 'value',
}) => {
  const handleChange = (e: DropdownChangeEvent) => {
    onChange(e.value);
  };

  return (
    <div style={{ width: fullWidth ? '100%' : 'auto' }}>
      {label && (
        <label style={{
          display: 'block',
          fontSize: '0.85rem',
          fontWeight: 600,
          color: error ? '#dc3545' : '#0f3d3e',
          marginBottom: '6px',
          letterSpacing: '0.2px'
        }}>
          {label} {required && <span style={{ color: '#dc3545' }}>*</span>}
        </label>
      )}
      
      <div style={{ position: 'relative' }}>
        {icon && (
          <i 
            className={icon} 
            style={{
              position: 'absolute',
              left: '14px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#0f3d3e',
              fontSize: '1rem',
              pointerEvents: 'none',
              zIndex: 1,
              opacity: 0.5
            }}
          ></i>
        )}
        
        <Dropdown
          value={value}
          onChange={handleChange}
          options={options}
          optionLabel={optionLabel}
          optionValue={optionValue}
          placeholder={placeholder}
          disabled={disabled}
          filter={filter}
          showClear={showClear}
          style={{
            width: '100%',
            height: '44px',
            border: `1px solid ${error ? '#dc3545' : '#e5e7eb'}`,
            borderRadius: '10px',
            background: disabled ? '#f8f9fa' : 'white',
          }}
          panelStyle={{
            borderRadius: '10px',
            boxShadow: '0 10px 40px rgba(15, 61, 62, 0.15)',
            marginTop: '4px',
            border: '1px solid #e5e7eb'
          }}
          className={`global-dropdown ${icon ? 'has-icon' : ''}`}
        />
      </div>
      
      {error && (
        <span style={{
          display: 'block',
          marginTop: '6px',
          fontSize: '0.75rem',
          color: '#dc3545',
          fontWeight: 500
        }}>
          {error}
        </span>
      )}

      <style>{`
        .global-dropdown.p-dropdown,
        .global-dropdown .p-dropdown {
          transition: all 0.2s ease;
        }

        .global-dropdown.p-dropdown .p-dropdown-label,
        .global-dropdown .p-dropdown .p-dropdown-label,
        .global-dropdown.p-dropdown span.p-dropdown-label,
        .global-dropdown .p-dropdown span.p-dropdown-label {
          padding-right: 55px !important;
          padding-left: 14px !important;
          font-size: 0.9rem !important;
          font-weight: 500 !important;
          color: #333 !important;
          display: block !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
        }

        .global-dropdown.p-dropdown .p-inputtext,
        .global-dropdown .p-dropdown .p-inputtext {
          padding-right: 55px !important;
          padding-left: 14px !important;
          font-size: 0.9rem !important;
          font-weight: 500 !important;
        }

        .global-dropdown.p-dropdown .p-dropdown-label.p-placeholder,
        .global-dropdown .p-dropdown .p-dropdown-label.p-placeholder {
          color: #999 !important;
          opacity: 0.7;
        }

        .global-dropdown.has-icon.p-dropdown span.p-dropdown-label,
        .global-dropdown.has-icon .p-dropdown span.p-dropdown-label,
        .global-dropdown.has-icon.p-dropdown .p-inputtext,
        .global-dropdown.has-icon .p-dropdown .p-inputtext {
          padding-left: 42px !important;
        }

        .global-dropdown.p-dropdown.p-disabled .p-inputtext,
        .global-dropdown.p-dropdown.p-disabled .p-dropdown-label,
        .global-dropdown .p-dropdown.p-disabled .p-inputtext,
        .global-dropdown .p-dropdown.p-disabled .p-dropdown-label {
          color: #999 !important;
        }

        .global-dropdown.p-dropdown:not(.p-disabled):hover,
        .global-dropdown .p-dropdown:not(.p-disabled):hover {
          border-color: #1a5555 !important;
        }

        .global-dropdown.p-dropdown:not(.p-disabled).p-focus,
        .global-dropdown .p-dropdown:not(.p-disabled).p-focus {
          outline: 0;
          border-color: #0f3d3e !important;
          box-shadow: 0 0 0 3px rgba(15, 61, 62, 0.1) !important;
        }

        .global-dropdown.p-dropdown .p-dropdown-trigger,
        .global-dropdown .p-dropdown .p-dropdown-trigger {
          color: #0f3d3e;
          opacity: 0.5;
          width: 40px;
          flex-shrink: 0;
        }

        .global-dropdown .p-dropdown-panel .p-dropdown-items .p-dropdown-item {
          padding: 10px 14px;
          font-size: 0.85rem;
          color: #333;
          font-weight: 500;
          transition: all 0.15s ease;
          border-radius: 6px;
          margin: 2px 4px;
        }

        .global-dropdown .p-dropdown-panel .p-dropdown-items .p-dropdown-item:hover {
          background: rgba(15, 61, 62, 0.08);
          color: #0f3d3e;
        }

        .global-dropdown .p-dropdown-panel .p-dropdown-items .p-dropdown-item.p-highlight {
          background: linear-gradient(135deg, rgba(15, 61, 62, 0.1) 0%, rgba(26, 85, 85, 0.08) 100%);
          color: #0f3d3e;
          font-weight: 700;
        }

        .global-dropdown .p-dropdown-panel .p-dropdown-header {
          padding: 12px;
          border-bottom: 1px solid #e5e7eb;
          background: linear-gradient(135deg, rgba(15, 61, 62, 0.03) 0%, rgba(26, 85, 85, 0.03) 100%);
        }

        .global-dropdown .p-dropdown-panel .p-dropdown-header .p-dropdown-filter {
          padding: 8px 12px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          font-size: 0.85rem;
        }

        .global-dropdown .p-dropdown-panel .p-dropdown-header .p-dropdown-filter:focus {
          border-color: #0f3d3e;
          box-shadow: 0 0 0 3px rgba(15, 61, 62, 0.1);
          outline: none;
        }

        .global-dropdown .p-dropdown-clear-icon {
          color: #0f3d3e;
          opacity: 0.5;
          right: 40px;
        }

        .global-dropdown .p-dropdown-clear-icon:hover {
          color: #dc3545;
          opacity: 1;
        }

        .global-dropdown.p-disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Empty message styling */
        .global-dropdown .p-dropdown-panel .p-dropdown-empty-message {
          padding: 12px 14px;
          color: #999;
          font-size: 0.85rem;
          text-align: center;
        }

        /* Filter input icon */
        .global-dropdown .p-dropdown-panel .p-dropdown-header .p-dropdown-filter-icon {
          color: #0f3d3e;
          opacity: 0.5;
        }

        /* Scrollbar styling */
        .global-dropdown .p-dropdown-items-wrapper::-webkit-scrollbar {
          width: 6px;
        }

        .global-dropdown .p-dropdown-items-wrapper::-webkit-scrollbar-track {
          background: #f8f9fa;
          border-radius: 3px;
        }

        .global-dropdown .p-dropdown-items-wrapper::-webkit-scrollbar-thumb {
          background: #ccc;
          border-radius: 3px;
        }

        .global-dropdown .p-dropdown-items-wrapper::-webkit-scrollbar-thumb:hover {
          background: #0f3d3e;
        }
      `}</style>
    </div>
  );
};